import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DollarSign, Edit, GripVertical, Hash, Plus, X } from "lucide-react";
import { useId, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useComponentFormState } from "@/hooks/component-form/useComponentFormState";
import type {
  ChecklistComponent,
  ComponentOption,
  ComponentType,
  PricingType,
} from "@/types/components";

interface EditComponentModalEnhancedProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    label: string;
    component_type?: ComponentType;
    placeholder?: string;
    help_text?: string;
    is_required: boolean;
    default_value?: string;
    base_price?: number;
    pricing_type?: PricingType;
    pricing_unit_label?: string;
    allow_quantity_input?: boolean;
    options?: ComponentOption[];
  }) => void;
  component: ChecklistComponent;
  isUpdating: boolean;
}

// Sortable Option Component
function SortableOption({
  option,
  index,
  isDefault,
  onSetDefault,
  onRemove,
  onUpdatePrice,
  onUpdateLabel,
  isUpdating,
}: {
  option: ComponentOption;
  index: number;
  isDefault: boolean;
  onSetDefault: () => void;
  onRemove: () => void;
  onUpdatePrice: (price: number) => void;
  onUpdateLabel: (label: string) => void;
  isUpdating: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option.id ?? option.option_value ?? `option-${index}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(option.option_label);
  const [editPrice, setEditPrice] = useState((option.price_modifier || 0).toString());

  const handleSaveEdit = () => {
    onUpdateLabel(editLabel);
    onUpdatePrice(Number.parseFloat(editPrice) || 0);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditLabel(option.option_label);
    setEditPrice((option.price_modifier || 0).toString());
    setIsEditing(false);
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className={`p-3 ${isDragging ? "shadow-lg" : ""} ${isDefault ? "border-primary" : ""}`}>
        <div className="flex items-center gap-2">
          <div {...attributes} {...listeners} className="cursor-move">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>

          {isEditing ? (
            <>
              <Input
                className="flex-1"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                placeholder="Option label"
                disabled={isUpdating}
              />
              <Input
                className="w-24"
                type="number"
                inputMode="decimal"
                step="0.01"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                placeholder="Price"
                disabled={isUpdating}
              />
              <Button variant="ghost" size="sm" onClick={handleSaveEdit} disabled={isUpdating}>
                Save
              </Button>
              <Button variant="ghost" size="sm" onClick={handleCancelEdit} disabled={isUpdating}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <div className="flex-1">
                <p className="font-medium">{option.option_label}</p>
                <p className="text-xs text-muted-foreground">Value: {option.option_value}</p>
              </div>

              <div className="flex items-center gap-1 text-sm">
                <DollarSign className="h-3 w-3" />
                {option.price_modifier?.toFixed(2) || "0.00"}
              </div>

              {isDefault && <Badge variant="secondary">Default</Badge>}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                disabled={isUpdating}
              >
                <Edit className="h-4 w-4" />
              </Button>

              {!isDefault && (
                <Button variant="ghost" size="sm" onClick={onSetDefault} disabled={isUpdating}>
                  Set Default
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={onRemove}
                disabled={isUpdating}
                className="text-destructive hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

export function EditComponentModalEnhanced({
  isOpen,
  onClose,
  onConfirm,
  component,
  isUpdating,
}: EditComponentModalEnhancedProps) {
  // Use custom hook for form state management
  const formState = useComponentFormState({ component });

  const labelFieldId = useId();
  const placeholderFieldId = useId();
  const helpTextFieldId = useId();
  const requiredSwitchId = useId();
  const defaultValueFieldId = useId();
  const basePriceFieldId = useId();
  const pricingTypeFieldId = useId();
  const pricingUnitFieldId = useId();
  const quantitySwitchId = useId();
  const componentTypeFieldId = useId();

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleSubmit = () => {
    if (!formState.isValid()) {
      return;
    }

    onConfirm(formState.getFormData());
  };

  const handleClose = () => {
    onClose();
  };

  const supportsOptions = ["select", "radio", "checkbox"].includes(formState.componentType);
  const supportsPlaceholder = ["text", "number", "textarea"].includes(formState.componentType);
  const supportsDefaultValue = ["text", "number", "textarea"].includes(formState.componentType);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Component
          </DialogTitle>
          <DialogDescription>
            Update component settings, reorder options with drag-and-drop, and edit prices inline.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 pr-2">
          {/* Component Type Switcher */}
          <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
            <Label htmlFor={componentTypeFieldId} className="font-semibold">
              Component Type
            </Label>
            <Select
              value={formState.componentType}
              onValueChange={(value) => formState.setComponentType(value as ComponentType)}
              disabled={isUpdating}
            >
              <SelectTrigger id={componentTypeFieldId} className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text Input</SelectItem>
                <SelectItem value="number">Number Input</SelectItem>
                <SelectItem value="textarea">Text Area</SelectItem>
                <SelectItem value="select">Select Dropdown</SelectItem>
                <SelectItem value="radio">Radio Buttons</SelectItem>
                <SelectItem value="checkbox">Checkboxes (Multi-select)</SelectItem>
              </SelectContent>
            </Select>
            {formState.componentType !== component.component_type && (
              <p className="text-sm text-amber-700 mt-2">
                ⚠️ Changing component type will affect how this field appears in quotes.
              </p>
            )}
          </div>

          {/* Basic Fields */}
          <div>
            <Label htmlFor={labelFieldId}>Label *</Label>
            <Input
              id={labelFieldId}
              value={formState.label}
              onChange={(e) => formState.setLabel(e.target.value)}
              placeholder="e.g., Tile Size"
              disabled={isUpdating}
            />
          </div>

          {supportsPlaceholder && (
            <div>
              <Label htmlFor={placeholderFieldId}>Placeholder Text</Label>
              <Input
                id={placeholderFieldId}
                value={formState.placeholder}
                onChange={(e) => formState.setPlaceholder(e.target.value)}
                placeholder="e.g., Enter dimensions"
                disabled={isUpdating}
              />
            </div>
          )}

          <div>
            <Label htmlFor={helpTextFieldId}>Help Text</Label>
            <Textarea
              id={helpTextFieldId}
              value={formState.helpText}
              onChange={(e) => formState.setHelpText(e.target.value)}
              placeholder="Additional information for estimators"
              disabled={isUpdating}
              rows={2}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id={requiredSwitchId}
              checked={formState.isRequired}
              onCheckedChange={formState.setIsRequired}
              disabled={isUpdating}
            />
            <Label htmlFor={requiredSwitchId}>Required field</Label>
          </div>

          {supportsDefaultValue && (
            <div>
              <Label htmlFor={defaultValueFieldId}>Default Value</Label>
              <Input
                id={defaultValueFieldId}
                value={formState.defaultValue}
                onChange={(e) => formState.setDefaultValue(e.target.value)}
                placeholder="Default value for this field"
                disabled={isUpdating}
              />
            </div>
          )}

          {/* Quantity Toggle */}
          <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
            <div className="flex items-center space-x-2">
              <Switch
                id={quantitySwitchId}
                checked={formState.allowQuantityInput}
                onCheckedChange={formState.setAllowQuantityInput}
                disabled={isUpdating}
              />
              <Label htmlFor={quantitySwitchId} className="flex items-center gap-2">
                <Hash className="h-4 w-4" />
                Allow Quantity Input
              </Label>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              When enabled, estimators can specify quantities (e.g., "3 units of this item")
            </p>
          </div>

          {/* Pricing Settings for non-option components */}
          {!supportsOptions && (
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-medium flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Pricing Settings
              </h4>

              <div>
                <Label htmlFor={basePriceFieldId}>Base Price ($)</Label>
                <Input
                  id={basePriceFieldId}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={formState.basePrice}
                  onChange={(e) => formState.setBasePrice(e.target.value)}
                  placeholder="0.00"
                  disabled={isUpdating}
                />
              </div>

              <div>
                <Label htmlFor={pricingTypeFieldId}>Pricing Type</Label>
                <Select
                  value={formState.pricingType}
                  onValueChange={(value) => formState.setPricingType(value as PricingType)}
                  disabled={isUpdating}
                >
                  <SelectTrigger id={pricingTypeFieldId}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed Price</SelectItem>
                    <SelectItem value="per_unit">Per Unit</SelectItem>
                    <SelectItem value="per_character">Per Character</SelectItem>
                    <SelectItem value="custom">Custom Calculation</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formState.pricingType === "per_unit" && (
                <div>
                  <Label htmlFor={pricingUnitFieldId}>Unit Label</Label>
                  <Input
                    id={pricingUnitFieldId}
                    value={formState.pricingUnitLabel}
                    onChange={(e) => formState.setPricingUnitLabel(e.target.value)}
                    placeholder="e.g., per sq ft, per meter"
                    disabled={isUpdating}
                  />
                </div>
              )}
            </div>
          )}

          {/* Options with Drag-and-Drop */}
          {supportsOptions && (
            <div className="space-y-3 border-t pt-4">
              <Label className="font-semibold">Options (Drag to reorder)</Label>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={formState.handleDragEnd}
              >
                <SortableContext
                  items={formState.options.map(
                    (opt, idx) => opt.id ?? opt.option_value ?? `option-${idx}`
                  )}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {formState.options.map((option, index) => (
                      <SortableOption
                        key={option.id ?? option.option_value ?? `option-${index}`}
                        option={option}
                        index={index}
                        isDefault={option.is_default}
                        onSetDefault={() => formState.setDefaultOption(index)}
                        onRemove={() => formState.removeOption(index)}
                        onUpdatePrice={(price) => formState.updateOptionPrice(index, price)}
                        onUpdateLabel={(label) => formState.updateOptionLabel(index, label)}
                        isUpdating={isUpdating}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              <div className="space-y-2 pt-3">
                <p className="text-xs text-muted-foreground">Add new option with price</p>
                <div className="flex gap-2">
                  <Input
                    className="flex-1"
                    value={formState.newOptionLabel}
                    onChange={(e) => formState.setNewOptionLabel(e.target.value)}
                    placeholder="Option label"
                    disabled={isUpdating}
                    onKeyPress={(e) => e.key === "Enter" && formState.addOption()}
                  />
                  <Input
                    className="w-32"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    value={formState.newOptionPrice}
                    onChange={(e) => formState.setNewOptionPrice(e.target.value)}
                    placeholder="Price"
                    disabled={isUpdating}
                  />
                  <Button
                    type="button"
                    onClick={formState.addOption}
                    disabled={!formState.newOptionLabel.trim() || isUpdating}
                    size="sm"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isUpdating || !formState.isValid()}>
            {isUpdating ? "Updating..." : "Update Component"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
